import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Calendar, Heart, Award, Flag, AlertTriangle, Lightbulb, FileText,
  UserPlus, CheckCircle, ChevronDown, Filter, TrendingUp, Star,
  Clock, MessageCircle, ArrowLeft
} from 'lucide-react';
import PrincipalNav from '../components/PrincipalNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ChildTimeline = () => {
  const navigate = useNavigate();
  const { childId } = useParams();
  
  const [timeline, setTimeline] = useState([]);
  const [child, setChild] = useState(null);
  const [summary, setSummary] = useState(null);
  const [journeySummary, setJourneySummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [eventTypes, setEventTypes] = useState({});

  const getToken = () => {
    // Support both parent and principal access
    return localStorage.getItem('principal_token') || localStorage.getItem('parent_token');
  };

  const getNavComponent = () => {
    if (localStorage.getItem('principal_token')) return 'principal';
    if (localStorage.getItem('parent_token')) return 'parent';
    return null;
  };

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/principal/login');
      return;
    }
    loadTimeline();
    loadJourneySummary();
  }, [navigate, childId]);

  const loadTimeline = async () => {
    try {
      const token = getToken();
      const params = { token };
      if (selectedTypes.length > 0) {
        params.event_types = selectedTypes.join(',');
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/child/${childId}/timeline`, { params });
      setTimeline(response.data.timeline || []);
      setChild(response.data.child);
      setSummary(response.data.summary);
      setEventTypes(response.data.event_types || {});
    } catch (error) {
      console.error('Error loading timeline:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadJourneySummary = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${BACKEND_URL}/api/child/${childId}/journey-summary`, {
        params: { token }
      });
      setJourneySummary(response.data);
    } catch (error) {
      console.error('Error loading journey summary:', error);
    }
  };

  const toggleEventType = (type) => {
    setSelectedTypes(prev => {
      const newTypes = prev.includes(type) 
        ? prev.filter(t => t !== type) 
        : [...prev, type];
      return newTypes;
    });
  };

  useEffect(() => {
    if (!loading) {
      loadTimeline();
    }
  }, [selectedTypes]);

  const getEventIcon = (type) => {
    const icons = {
      session: Calendar,
      mood_entry: Heart,
      badge_earned: Award,
      milestone: Flag,
      insight: Lightbulb,
      report: FileText,
      enrollment: UserPlus
    };
    return icons[type] || Calendar;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const groupEventsByDate = (events) => {
    const groups = {};
    events.forEach(event => {
      const date = event.date?.substring(0, 10) || 'Unknown';
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(event);
    });
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PrincipalNav />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  const groupedEvents = groupEventsByDate(timeline);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100" data-testid="child-timeline-page">
      <PrincipalNav />
      
      <div className="pt-16 pb-8 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {child?.name}&apos;s Journey
              </h1>
              <p className="text-gray-600">
                Age {child?.age} • {journeySummary?.journey?.duration_days || 0} days on Sanjaya
              </p>
            </div>
          </div>

          {/* Journey Summary Cards */}
          {journeySummary && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <Card className="bg-white shadow-sm">
                <CardContent className="p-4 text-center">
                  <Calendar className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{journeySummary.stats?.total_sessions || 0}</p>
                  <p className="text-xs text-gray-500">Sessions</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white shadow-sm">
                <CardContent className="p-4 text-center">
                  <Heart className="w-8 h-8 text-pink-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{journeySummary.mood?.average || '—'}</p>
                  <p className="text-xs text-gray-500">Avg Mood</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white shadow-sm">
                <CardContent className="p-4 text-center">
                  <Award className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{journeySummary.stats?.badges_earned || 0}</p>
                  <p className="text-xs text-gray-500">Badges</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white shadow-sm">
                <CardContent className="p-4 text-center">
                  <Star className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{journeySummary.child?.total_points || 0}</p>
                  <p className="text-xs text-gray-500">Points</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white shadow-sm">
                <CardContent className="p-4 text-center">
                  <TrendingUp className={`w-8 h-8 mx-auto mb-2 ${
                    journeySummary.mood?.trend === 'improving' ? 'text-green-500' :
                    journeySummary.mood?.trend === 'declining' ? 'text-red-500' : 'text-yellow-500'
                  }`} />
                  <p className="text-lg font-bold text-gray-900 capitalize">{journeySummary.mood?.trend || 'Stable'}</p>
                  <p className="text-xs text-gray-500">Mood Trend</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Event Type Filters */}
          <Card className="mb-6 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filter by event type:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(eventTypes).map(([type, info]) => (
                  <button
                    key={type}
                    onClick={() => toggleEventType(type)}
                    className={`px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 transition-all ${
                      selectedTypes.length === 0 || selectedTypes.includes(type)
                        ? 'text-white'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                    style={{
                      backgroundColor: selectedTypes.length === 0 || selectedTypes.includes(type) 
                        ? info.color 
                        : undefined
                    }}
                  >
                    {React.createElement(getEventIcon(type), { className: "w-3.5 h-3.5" })}
                    {info.label}
                    {summary?.event_counts?.[type] && (
                      <span className="ml-1 opacity-75">({summary.event_counts[type]})</span>
                    )}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Timeline */}
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>

            {groupedEvents.length === 0 ? (
              <Card className="ml-12">
                <CardContent className="p-8 text-center">
                  <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700">No Events Yet</h3>
                  <p className="text-gray-500">Events will appear here as the child&apos;s journey progresses.</p>
                </CardContent>
              </Card>
            ) : (
              groupedEvents.map(([date, events]) => (
                <div key={date} className="mb-8">
                  {/* Date header */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-white border-4 border-blue-200 flex items-center justify-center z-10">
                      <Calendar className="w-5 h-5 text-blue-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800">{formatDate(date)}</h3>
                  </div>

                  {/* Events for this date */}
                  <div className="ml-12 space-y-3">
                    {events.map((event, idx) => {
                      const Icon = getEventIcon(event.type);
                      const typeInfo = eventTypes[event.type] || {};
                      
                      return (
                        <Card 
                          key={event.id || idx} 
                          className="shadow-sm hover:shadow-md transition-shadow border-l-4"
                          style={{ borderLeftColor: typeInfo.color || '#gray' }}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <div 
                                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: `${typeInfo.color}20` }}
                              >
                                <Icon className="w-5 h-5" style={{ color: typeInfo.color }} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-semibold text-gray-900">{event.title}</h4>
                                  <span 
                                    className="px-2 py-0.5 rounded-full text-xs"
                                    style={{ 
                                      backgroundColor: `${typeInfo.color}20`,
                                      color: typeInfo.color
                                    }}
                                  >
                                    {typeInfo.label}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600">{event.description}</p>
                                
                                {/* Metadata */}
                                {event.metadata && Object.keys(event.metadata).length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-2">
                                    {event.metadata.observer_name && (
                                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                        with {event.metadata.observer_name}
                                      </span>
                                    )}
                                    {event.metadata.duration_minutes && (
                                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                        <Clock className="w-3 h-3 inline mr-1" />
                                        {event.metadata.duration_minutes} mins
                                      </span>
                                    )}
                                    {event.metadata.mood_rating && (
                                      <span className="text-xs bg-pink-100 text-pink-600 px-2 py-1 rounded">
                                        Mood: {event.metadata.mood_rating}/5
                                      </span>
                                    )}
                                    {event.metadata.points && (
                                      <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                                        +{event.metadata.points} points
                                      </span>
                                    )}
                                    {event.metadata.topics?.length > 0 && (
                                      <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded">
                                        <MessageCircle className="w-3 h-3 inline mr-1" />
                                        {event.metadata.topics.slice(0, 3).join(', ')}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChildTimeline;
